/**
 * Custom Component Registrations
 *
 * This file registers all custom React components that can be rendered
 * via CMS using the custom_react_component block type.
 *
 * Components are lazy-loaded to optimize bundle size and initial load time.
 *
 * To add a new component:
 * 1. Create a lazy import using React.lazy()
 * 2. Call registerCustomComponent with a unique slug and the lazy component
 * 3. Use the same slug in DatoCMS reactComponentSlug field
 */

import React from 'react';
import { registerCustomComponent } from './customComponentRegistry';

// Lazy-loaded SuccessReport component
// CMS can reference this via reactComponentSlug: 'success-report'
const LazySuccessReport = React.lazy(
  () => import('containers/SuccessReport/SuccessReport')
);

// Lazy-loaded ValueCreationFilters component (combines IntegratedProductFilter + CategoryFilter)
// CMS can reference this via reactComponentSlug: 'value-creation-filters'
const LazyValueCreationFilters = React.lazy(
  () => import('components/ValueCreationFilters/ValueCreationFilters')
);

// Lazy-loaded CategoryFilter component
// CMS can reference this via reactComponentSlug: 'category-filter'
const LazyCategoryFilter = React.lazy(
  () => import('components/CategoryFilter/CategoryFilter')
);
const LazyIncidentMonitoring = React.lazy(
  () =>
    import(
      '@containers/ApplicationPerformanceRedesigned/IncidentMonitoringConfigRedesigned'
    )
);

// Lazy-loaded AppPerformanceO9Solutions component
// CMS can reference this via reactComponentSlug: 'app-performance-o9-solutions'
const LazyAppPerformanceO9Solutions = React.lazy(
  () =>
    import(
      '@containers/ApplicationPerformanceRedesigned/AppPerformanceO9Solutions'
    )
);

// Lazy-loaded SuggestedOrderWidget component
// CMS can reference this via reactComponentSlug: 'suggested-order-widget'
const LazySuggestedOrderWidget = React.lazy(
  () => import('containers/ValueRealization/SuggestedOrderWidget')
);

// Lazy-loaded O9SolutionsValueCreation component
// CMS can reference this via reactComponentSlug: 'o9solutions-value-creation'
const LazyO9SolutionsValueCreation = React.lazy(
  () => import('containers/ValueCreationMetrics/O9SolutionsValueCreation')
);

// Lazy-loaded AdoptionKpisWidget component
// CMS can reference this via reactComponentSlug: 'adoption-kpis-widget'
const LazyAdoptionKpisWidget = React.lazy(
  () => import('containers/ValueRealization/AdoptionKpisWidget')
);

// Lazy-loaded TechnicalKpisWidget component
// CMS can reference this via reactComponentSlug: 'technical-kpis-widget'
const LazyTechnicalKpisWidget = React.lazy(
  () => import('containers/ValueRealization/TechnicalKpisWidget')
);

// Lazy-loaded AIQLocalFilters component
// CMS can reference this via reactComponentSlug: 'aiq-local-filters'
const LazyAIQLocalFilters = React.lazy(
  () => import('containers/UserBehaviorEngagement/AIQ/LocalFilters')
);

// Lazy-loaded AIQ UserAdoption component (wrapped with AIQLocalProvider for CMS usage)
// CMS can reference this via reactComponentSlug: 'aiq-user-adoption'
const LazyAIQUserAdoption = React.lazy(
  () => import('containers/UserBehaviorEngagement/AIQ/UserAdoptionCMS')
);

// Lazy-loaded AIQ UserBehavior component (wrapped with AIQLocalProvider for CMS usage)
// CMS can reference this via reactComponentSlug: 'aiq-user-behavior'
const LazyAIQUserBehavior = React.lazy(
  () => import('containers/UserBehaviorEngagement/AIQ/UserBehaviorCMS')
);

// Lazy-loaded DivisionFilter component for AFH Overview
// CMS can reference this via reactComponentSlug: 'afh-division-filter'
const LazyDivisionFilter = React.lazy(
  () => import('components/DivisionFilter/DivisionFilter')
);

// Lazy-loaded TrendInsightsCMS component for AFH Overview
// CMS can reference this via reactComponentSlug: 'afh-trend-insights'
const LazyTrendInsightsCMS = React.lazy(
  () =>
    import(
      'containers/OverView/AFHOverview/SteerCoReportingHoc/TrendInsigts/TrendInsightsCMS'
    )
);

// Lazy-loaded MenuProUserAdoption component for MenuPro User Behavior & Engagement
// CMS can reference this via reactComponentSlug: 'menupro-user-adoption'
const LazyMenuProUserAdoption = React.lazy(
  () => import('containers/UserBehaviorEngagement/MenuPro')
);

// Lazy-loaded ProfitToServeOverview component for CTX ProfitToServe Overview
// CMS can reference this via reactComponentSlug: 'profit-to-serve-overview'
const LazyProfitToServeOverview = React.lazy(() =>
  import('service/overviewService/programs/CTX/ProfitToServe').then(
    (module) => ({
      default: module.ProfitToServeOverview,
    })
  )
);

// Lazy-loaded ApplicationPerformanceWrapper for App Performance tab
// CMS can reference this via reactComponentSlug: 'app-performance-wrapper'
const LazyApplicationPerformanceWrapper = React.lazy(
  () =>
    import(
      'containers/ApplicationPerformanceRedesigned/ApplicationPerformanceWrapper'
    )
);

// Lazy-loaded UserAdoptionWrapper for User Adoption tab
// CMS can reference this via reactComponentSlug: 'user-adoption-wrapper'
const LazyUserAdoptionWrapper = React.lazy(
  () => import('containers/UserBehaviorEngagement/UserAdoptionWrapper')
);

// Lazy-loaded NBAValueCreation component for Value Creation Metrics
// CMS can reference this via reactComponentSlug: 'nba-value-creation'
const LazyNBAValueCreation = React.lazy(
  () => import('containers/ValueCreationMetrics/NBAValueCreation')
);

// Lazy-loaded GlobalUserAdoption component for o9Solutions User Adoption
// CMS can reference this via reactComponentSlug: 'o9solutions-user-adoption'
const LazyO9SolutionsUserAdoption = React.lazy(
  () => import('containers/GlobalUserAdoption')
);

// Lazy-loaded B2BPepsiConnectOverview component for B2B Overview
// CMS can reference this via reactComponentSlug: 'b2b-pepsiconnect-overview'
const LazyB2BPepsiConnectOverview = React.lazy(
  () => import('containers/OverView/B2BOverview')
);

// Lazy-loaded B2BValueCreation component for B2B Value Creation Metrics
// CMS can reference this via reactComponentSlug: 'b2b-value-creation'
const LazyB2BValueCreation = React.lazy(
  () => import('containers/ValueCreationMetrics/B2BValueCreation')
);

registerCustomComponent('success-report', LazySuccessReport);
registerCustomComponent('gs-so-category-filter', LazyCategoryFilter); // GS SO Overview category filter
registerCustomComponent(
  'gs-so-value-creation-filters',
  LazyValueCreationFilters
); // GS SO integrated product filter
registerCustomComponent('incident-monitoring', LazyIncidentMonitoring);
registerCustomComponent(
  'o9solutions-app-performance',
  LazyAppPerformanceO9Solutions
); // IBP o9Solutions App Performance
registerCustomComponent('suggested-order-widget', LazySuggestedOrderWidget); // GS Value Realization Suggested Order widget
registerCustomComponent('adoption-kpis-widget', LazyAdoptionKpisWidget); // GS Value Realization Adoption KPIs widget
registerCustomComponent('technical-kpis-widget', LazyTechnicalKpisWidget); // GS Value Realization Technical KPIs widget
registerCustomComponent('aiq-local-filters', LazyAIQLocalFilters); // AIQ User Engagement local filters
registerCustomComponent('aiq-user-adoption', LazyAIQUserAdoption); // AIQ User Adoption tab content
registerCustomComponent('aiq-user-behavior', LazyAIQUserBehavior); // AIQ User Behavior tab content
registerCustomComponent('afh-division-filter', LazyDivisionFilter); // AFH Overview division filter
registerCustomComponent('afh-menu-pro-trend-insights', LazyTrendInsightsCMS); // AFH Overview Trend Insights component
registerCustomComponent('afh-menu-pro-user-behavior', LazyMenuProUserAdoption); // MenuPro User Behavior & Engagement
registerCustomComponent('profit-to-serve-overview', LazyProfitToServeOverview); // CTX ProfitToServe Overview
registerCustomComponent(
  'app-performance-wrapper',
  LazyApplicationPerformanceWrapper
); // App Performance tab (dynamic based on product/program)
registerCustomComponent('user-adoption-wrapper', LazyUserAdoptionWrapper); // User Adoption tab (dynamic based on product)
registerCustomComponent('nba-value-creation', LazyNBAValueCreation); // NBA Value Creation Metrics
registerCustomComponent(
  'o9solutions-value-creation',
  LazyO9SolutionsValueCreation
); // IBP o9Solutions Value Creation
registerCustomComponent('global-user-adoption', LazyO9SolutionsUserAdoption); // IBP o9Solutions User Adoption
registerCustomComponent(
  'b2b-pepsiconnect-overview',
  LazyB2BPepsiConnectOverview
); // B2B PepsiConnect Overview
registerCustomComponent('b2b-value-creation', LazyB2BValueCreation); // B2B Value Creation Metrics
